import React, { FC, ReactNode, useEffect, useMemo, useState } from "react";
import Breadcrumbs from "@/components/Breadcrumbs";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import Card from "@mui/material/Card";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import Button from "@mui/material/Button";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Chip from "@mui/material/Chip";
import ActionMenu from "@/components/ActionMenu";
import { AlertDialog } from "@/components/AlertDialog/AlertDialog";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import LightTooltip from "@/components/LightTooltip";
import {
  useDeleteProgram,
  useGetPrograms,
} from "@/hooks/management/usePrograms.manage";
import { Program } from "@/domain/programs.interface";
import { ProgramStatus } from "@/domain/participant.enum";
import SearchBox from "@/components/SearchBox";
import { useNavigate } from "react-router-dom";
import useDebounce from "@/hooks/useDebounce";
import { useMaxNumberPrograms } from "@/hooks/useSetting";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";

const ProgramsPage: FC = () => {
  const navigate = useNavigate();
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("program");
  const { showSnackBar, showSimpleAlert } = useFeedBack();
  const { userInfo, userHasAccessToCode } = useUserStore();
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);

  const createAuditLog = useCreateAuditlog();
  const deleteProgram = useDeleteProgram();
  const auditLogViewCalledRef = React.useRef(false);
  const maxNumberPrograms = useMaxNumberPrograms();
  const maxNumberProgramsMemo = useMemo(
    () => maxNumberPrograms,
    [maxNumberPrograms],
  );

  useEffect(() => {
    setPageTitle(t("title"));
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    sortField: "name",
    sortOrder: 1,
    pageNumber: 0,
    pageSize: 10,
  });

  const [showDeleteModal, setShowDeleteModal] = useState<{
    open: boolean;
    program: Program | null;
  }>({
    open: false,
    program: null,
  });

  const searchDebounce = useDebounce({
    value: filters.search,
    delay: 500,
  });

  const { data, isLoadingPrograms, isFetchingPrograms, refetchPrograms } =
    useGetPrograms({
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      search: searchDebounce,
    });

  const headCells = useMemo(() => {
    const baseHeadCells: HeadCell[] = [
      {
        id: "name",
        isSortable: true,
        disablePadding: true,
        label: t("header.name"),
        align: "left",
        width: "180px",
      },
      {
        id: "status",
        isSortable: true,
        disablePadding: false,
        label: t("header.status"),
        align: "left",
      },
      {
        id: "actions",
        isSortable: false,
        disablePadding: false,
        label: t("header.action"),
        align: "right",
        width: "80px",
      },
    ];

    return baseHeadCells;
  }, [data]);

  const rows = useMemo(() => {
    if (data && data.programs.length === 0) return [];

    const programs = data?.programs;

    return (
      programs?.map((program) => {
        const actionMenu = [
          {
            value: t("buttons.edit"),
            onClick: () => {
              if (!userHasAccessToCode("PROG_EDIT")) {
                setShowPermissionDeniedModal(true);
                return;
              }
              navigate(`/programs/${program._id}`);
            },
            sx: {
              justifyContent: "center",
            },
          },
          // {
          //   value: t("buttons.archive"),
          //   onClick: () => {
          //     setSelectedProgram(program);
          //     setShowUpdateModal(true);
          //   },
          //   sx: {
          //     justifyContent: "center",
          //   },
          // },
          {
            value: t("buttons.delete"),
            onClick: () => {
              if (!userHasAccessToCode("PROG_DEL")) {
                setShowPermissionDeniedModal(true);
                return;
              }
              setShowDeleteModal({ open: true, program });
            },
            sx: {
              color: theme.palette.error.main,
              justifyContent: "center",
            },
          },
        ];

        const record: Record<string, ReactNode> = {
          name: <div>{program.name}</div>,
          status: (
            <div className="flex gap-1">
              <Chip
                sx={{
                  bgcolor:
                    program.status === "ACTIVE"
                      ? theme.palette.brand.light
                      : theme.palette.warning.light,
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor:
                    program.status === "ACTIVE"
                      ? theme.palette.brand.main
                      : theme.palette.warning.dark,
                }}
                label={
                  <div className="text-xs">
                    {
                      ProgramStatus[
                        program.status as keyof typeof ProgramStatus
                      ]
                    }
                  </div>
                }
              />
            </div>
          ),
          actions: <ActionMenu id={program._id} options={actionMenu} />,
        };

        const row = createDataRow(program._id as string, record);

        return row;
      }) || []
    );
  }, [data]);

  const handleDelete = async () => {
    await deleteProgram.mutateAsync(showDeleteModal.program?._id as string, {
      onSuccess: () => {
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.PROGRAM,
          option: OPTIONS.PROGRAM_DELETE,
          actionCode: ACTION_CODE.WEB_PROG_DELETE,
          action: "When deleting",
          detail: `Delete program ${showDeleteModal.program?.name}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        refetchPrograms();
        setShowDeleteModal({ open: false, program: null });
        showSnackBar(t("messages.delete-success"), "success");
      },
    });
  };

  const handleAddClick = () => {
    if (!userHasAccessToCode("PROG_ADD")) {
      setShowPermissionDeniedModal(true);
      return;
    }
    if (data?.totalCount && data?.totalCount > maxNumberProgramsMemo) {
      showSimpleAlert(
        t("messages.max-number-programs", { num: maxNumberProgramsMemo }),
      );
      return;
    }
    navigate("/programs/create");
  };

  useEffect(() => {
    if (userInfo && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.PROGRAM,
        option: OPTIONS.PROGRAM_LIST,
        actionCode: ACTION_CODE.WEB_PROG_LIST_VIEW,
        action: "When entering",
        detail: "Viewed the list of programs",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo && searchDebounce) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.PROGRAM,
        option: OPTIONS.PROGRAM_LIST,
        actionCode: ACTION_CODE.WEB_PROG_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of programs",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [userInfo, searchDebounce]);

  return (
    <Box>
      <Breadcrumbs
        items={[
          {
            label: t("page-title"),
            href: "/programs",
            isLink: true,
          },
        ]}
      />

      <Box
        mt={1}
        mb={4}
        sx={{
          width: "100%",
        }}
        display="flex"
        justifyContent="space-between"
        alignItems={"center"}
      >
        <Typography
          color={theme.palette.textMain.dark2}
          variant="headlineSmall"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {t("page-title")}

          <LightTooltip title={t("messages.information")} arrow theme={theme}>
            <InfoOutlinedIcon
              sx={{
                cursor: "pointer",
              }}
            />
          </LightTooltip>
        </Typography>
      </Box>

      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <SearchBox
              theme={theme}
              onChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  search: value,
                  pageNumber: 0,
                }));
              }}
              value={filters.search}
              placeHolder={t("place-holders.search-programs")}
            />

            <Box display="flex" gap="0.5rem">
              {isLoadingPrograms ? (
                <SkeletonHeaderButtons />
              ) : (
                <>
                  <Button
                    size={"medium"}
                    onClick={handleAddClick}
                    variant="contained"
                    disabled={isLoadingPrograms}
                    sx={{
                      borderRadius: "8px",
                      textAlign: "center",
                      height: "56px",
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.textMain.light,
                    }}
                  >
                    {t("buttons.add")}
                  </Button>
                </>
              )}
            </Box>
          </Box>
          <DataTable
            data={rows}
            headCells={headCells}
            order={filters.sortOrder === 1 ? "asc" : "desc"}
            orderBy={filters.sortField}
            rowsPerPage={filters.pageSize}
            page={filters.pageNumber}
            total={data?.totalCount}
            isLoading={isLoadingPrograms || isFetchingPrograms}
            onChangePage={(event, newPage) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: newPage,
              }));
            }}
            onChangeSize={(event) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                pageSize: Number(event.target.value),
              }));
            }}
            onChangeSort={(order, orderBy) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                sortField: orderBy as string,
                sortOrder: order === "asc" ? 1 : -1,
              }));
            }}
          />
        </CardContent>
      </Card>

      {showDeleteModal.open && (
        <AlertDialog
          open={showDeleteModal.open}
          title={t("messages.delete-title")}
          description={t("messages.delete-confirm")}
          onCancel={() => {
            setShowDeleteModal({ open: false, program: null });
          }}
          onConfirm={handleDelete}
          confirmText={t("buttons.confirm")}
          cancelText={t("buttons.cancel")}
        />
      )}

      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />
    </Box>
  );
};

export default ProgramsPage;
